import { Download, Check } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import type { OSType } from '../../hooks/useOSDetection';
import type { Arch } from './types';

interface DownloadFormat {
  type: string;
  arch?: Arch;
  filename: string;
  size: string;
  url: string;
}

interface DownloadCardProps {
  platform: OSType;
  icon: React.ReactNode;
  isRecommended: boolean;
  formats: DownloadFormat[];
  /** Detected CPU architecture. Used on macOS to highlight the matching DMG button. */
  detectedArch?: Arch | null;
  onDownload: (url: string, filename: string) => void;
}

const ARCH_LABELS: Record<Arch, string> = {
  arm64: 'Apple Silicon (M1/M2/M3/M4)',
  x64: 'Intel',
  universal: 'Universal',
};

export function DownloadCard({
  platform,
  icon,
  isRecommended,
  formats,
  detectedArch,
  onDownload,
}: DownloadCardProps) {
  const { t } = useTranslation();

  const platformKey = platform === 'unknown' ? 'linux' : platform;
  const archFormats = formats.filter((f) => f.arch);
  // The hint and the arch-aware highlighting only make sense when there is an
  // actual choice to make. macOS currently ships Apple Silicon only, so a
  // single arch format must still be highlighted as THE download, not left
  // unstyled waiting for a match (and browsers without userAgentData - Safari,
  // Firefox - would never produce that match anyway).
  const hasArchChoice = archFormats.length > 1;
  const showArchHint = hasArchChoice && !detectedArch;
  // Detected a CPU we publish no build for (an Intel Mac now that macOS is
  // Apple Silicon only). Say so instead of handing over a 2GB download that
  // cannot launch.
  const archUnsupported =
    archFormats.length > 0 &&
    detectedArch != null &&
    !archFormats.some((f) => f.arch === detectedArch);

  return (
    <div
      className={`relative flex flex-col p-6 rounded-xl border-2 transition-all duration-200 ${
        isRecommended
          ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-900/20 shadow-lg'
          : 'border-secondary-200 dark:border-secondary-700 bg-white dark:bg-secondary-800'
      }`}
    >
      {/* Recommended Badge */}
      {isRecommended && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded-full">
            <Check className="h-3 w-3" />
            {t('download.recommendedBadge')}
          </span>
        </div>
      )}

      {/* Platform Icon */}
      <div className="flex justify-center mb-4 mt-2">
        <div
          className={`w-16 h-16 rounded-xl flex items-center justify-center ${
            isRecommended
              ? 'bg-blue-100 dark:bg-blue-800/50'
              : 'bg-secondary-100 dark:bg-secondary-700'
          }`}
        >
          {icon}
        </div>
      </div>

      {/* Platform Name */}
      <h3 className="text-xl font-bold text-center text-secondary-900 dark:text-secondary-100 mb-1">
        {t(`download.platforms.${platformKey}.name`)}
      </h3>

      {/* Min Version */}
      <p className="text-sm text-center text-secondary-500 dark:text-secondary-400 mb-6">
        {t(`download.platforms.${platformKey}.minVersion`)}
      </p>

      {/* Arch hint when we couldn't auto-detect the user's CPU */}
      {showArchHint && (
        <p className="text-xs text-center text-secondary-600 dark:text-secondary-400 mb-3 px-2">
          {t('download.archHint')}
        </p>
      )}

      {/* Detected CPU has no published build */}
      {archUnsupported && (
        <p className="text-xs text-center text-amber-700 dark:text-amber-400 mb-3 px-2">
          {t('download.archUnsupported')}
        </p>
      )}

      {/* Download Buttons */}
      <div className="flex flex-col gap-2 mt-auto">
        {formats.map((format) => {
          const archMatches =
            format.arch !== undefined && detectedArch !== null && detectedArch !== undefined
              ? format.arch === detectedArch
              : false;
          const buttonKey = format.arch ? `${format.type}-${format.arch}` : format.type;
          const highlight = isRecommended && !archUnsupported && (!hasArchChoice || archMatches);
          return (
            <button
              key={buttonKey}
              onClick={() => onDownload(format.url, format.filename)}
              className={`flex items-center justify-between w-full px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                highlight
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-secondary-100 dark:bg-secondary-700 hover:bg-secondary-200 dark:hover:bg-secondary-600 text-secondary-900 dark:text-secondary-100'
              }`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <Download className="h-4 w-4 flex-shrink-0" />
                <span className="truncate text-left">
                  {format.arch ? ARCH_LABELS[format.arch] : format.type}
                </span>
                {archMatches && (
                  <Check className="h-4 w-4 flex-shrink-0" aria-label="matches your Mac" />
                )}
              </div>
              <span className="text-sm opacity-75 flex-shrink-0 ml-2">{format.size}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
